"use client";

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { AlertDialogWrapper } from "@components/common/containers/alert-dialog-wrapper";
import { Button } from "@components/ui/button";
import { Checkbox } from "@components/ui/checkbox";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";
import { AdminDestroyConfirmation } from "@features/personal-access-tokens/ui/admin-destroy-confirmation";
import { buildIssueRequest, validateIssueForm, type IssueFormValues } from "@features/personal-access-tokens/personal-access-token-issue-model";
import { PersonalAccessTokenScopeValues, type IssuedPersonalAccessToken, type IssuePersonalAccessTokenRequest, type PersonalAccessTokenScope } from "@features/personal-access-tokens/types";
export interface PersonalAccessTokenWorkspaceOption {
  readonly id: string;
  readonly label: string;
}
interface PersonalAccessTokenIssueDialogProps {
  readonly isOpen: boolean;
  readonly mode: "owner" | "admin";
  readonly allowedScopes: readonly PersonalAccessTokenScope[];
  readonly workspaceOptions: readonly PersonalAccessTokenWorkspaceOption[];
  readonly workspaceOptionsLoading?: boolean;
  readonly workspaceOptionsUnavailable?: boolean;
  readonly isRetryingWorkspaceOptions?: boolean;
  readonly onRetryWorkspaceOptions?: () => void;
  readonly isRetryingAdmittedScopes?: boolean;
  readonly onRetryAdmittedScopes?: () => void;
  readonly adminContext?: {
    readonly ownerName: string;
    readonly issuerName: string;
    readonly destroyConfirmationValue: string;
  };
  readonly onIssue: (request: IssuePersonalAccessTokenRequest) => Promise<IssuedPersonalAccessToken>;
  readonly onClose: () => void;
}
type IssueServerError = "ambiguous" | "limit-reached" | "already-issued" | "owner-not-found" | "scope-not-admitted" | "workspace-not-accessible" | "expiry-out-of-range";
type IssueErrorMessageKey = "issue.errors.ambiguousOutcome" | "issue.errors.limitReached" | "issue.errors.alreadyIssued" | "issue.errors.ownerNotFound" | "issue.errors.scopeNotAdmitted" | "issue.errors.scopeNotAdmittedAdmin" | "issue.errors.workspaceNotAccessible" | "issue.errors.expiryOutOfRange";
type WorkspaceChoice = "all" | "selected" | "";
const expiryPresets = [{
  days: 7,
  key: "days7"
}, {
  days: 30,
  key: "days30"
}, {
  days: 90,
  key: "days90"
}, {
  days: 365,
  key: "days365"
}] as const;
const getIssueErrorMessageKey = (error: IssueServerError, mode: "owner" | "admin"): IssueErrorMessageKey => {
  switch (error) {
    case "ambiguous":
      return "issue.errors.ambiguousOutcome";
    case "limit-reached":
      return "issue.errors.limitReached";
    case "already-issued":
      return "issue.errors.alreadyIssued";
    case "owner-not-found":
      return "issue.errors.ownerNotFound";
    case "scope-not-admitted":
      return mode === "admin" ? "issue.errors.scopeNotAdmittedAdmin" : "issue.errors.scopeNotAdmitted";
    case "workspace-not-accessible":
      return "issue.errors.workspaceNotAccessible";
    case "expiry-out-of-range":
      return "issue.errors.expiryOutOfRange";
  }
};
const readIssueErrorCode = (error: unknown): string | undefined => {
  if (typeof error !== "object" || error === null || !("data" in error)) return undefined;
  const data = error.data;
  if (typeof data !== "object" || data === null || !("code" in data)) return undefined;
  return typeof data.code === "string" ? data.code : undefined;
};
const createIdempotencyKey = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};
const isIssueScopeAllowed = (scope: PersonalAccessTokenScope, mode: "owner" | "admin", allowedScopes: readonly PersonalAccessTokenScope[]): boolean => allowedScopes.includes(scope) && (mode === "admin" || scope !== "api_destroy");
export const PersonalAccessTokenIssueDialog = ({
  isOpen,
  mode,
  allowedScopes,
  workspaceOptions,
  workspaceOptionsLoading = false,
  workspaceOptionsUnavailable = false,
  isRetryingWorkspaceOptions = false,
  onRetryWorkspaceOptions,
  isRetryingAdmittedScopes = false,
  onRetryAdmittedScopes,
  adminContext,
  onIssue,
  onClose
}: PersonalAccessTokenIssueDialogProps) => {
  const t = useTranslations("features.personal-access-tokens.ui.ownerTokensPage");
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<PersonalAccessTokenScope[]>([]);
  const [workspaceChoice, setWorkspaceChoice] = useState<WorkspaceChoice>("");
  const [workspaceIds, setWorkspaceIds] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState("");
  const [serverError, setServerError] = useState<IssueServerError | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [previousAttempt, setPreviousAttempt] = useState<{
    readonly fingerprint: string;
    readonly request: IssuePersonalAccessTokenRequest;
  } | null>(null);
  const [destroyConfirmation, setDestroyConfirmation] = useState("");
  const visibleScopes = useMemo(() => PersonalAccessTokenScopeValues.filter(scope => isIssueScopeAllowed(scope, mode, allowedScopes) || mode === "owner" && scope !== "api_destroy" && scopes.includes(scope)), [allowedScopes, mode, scopes]);
  const mcpAvailable = visibleScopes.some(scope_0 => scope_0.startsWith("mcp_"));
  const isConflict = serverError === "already-issued";
  const isAmbiguousOutcome = serverError === "ambiguous";
  const isFormLocked = isPending || isConflict || isAmbiguousOutcome;
  const form: IssueFormValues = {
    name,
    scopes,
    workspaceIds: workspaceChoice === "all" ? [] : workspaceIds,
    expiresAt
  };
  const validationErrors = validateIssueForm(form);
  const hasDisallowedScope = scopes.some(scope_1 => !isIssueScopeAllowed(scope_1, mode, allowedScopes));
  const hasWorkspaceChoice = workspaceChoice === "all" || workspaceChoice === "selected" && workspaceIds.length > 0 && workspaceIds.every(workspaceId => workspaceOptions.some(option => option.id === workspaceId));
  const requiresDestroyConfirmation = mode === "admin" && scopes.includes("api_destroy");
  const hasDestroyConfirmation = !requiresDestroyConfirmation || adminContext !== undefined && destroyConfirmation === adminContext.destroyConfirmationValue;
  const isFormValid = Object.keys(validationErrors).length === 0 && !hasDisallowedScope && hasWorkspaceChoice && hasDestroyConfirmation;
  const expiryValidationKey = validationErrors.expiresAt === "not-in-future" ? "notInFuture" : validationErrors.expiresAt === "beyond-maximum" ? "beyondMaximum" : "invalidDate";
  const idPrefix = mode === "owner" ? "owner-pat-issue" : "admin-pat-issue";
  const clearEditableError = () => {
    if (!isConflict) setServerError(null);
  };
  const resetForm = () => {
    setName("");
    setScopes([]);
    setWorkspaceChoice("");
    setWorkspaceIds([]);
    setExpiresAt("");
    setServerError(null);
    setPreviousAttempt(null);
    setDestroyConfirmation("");
  };
  const handleClose = () => {
    if (isConflict) resetForm();
    onClose();
  };
  const toggleScope = (scope_2: PersonalAccessTokenScope) => {
    if (isFormLocked) return;
    clearEditableError();
    setScopes(current => current.includes(scope_2) ? current.filter(item => item !== scope_2) : [...current, scope_2]);
  };
  const toggleWorkspace = (workspaceId_0: string) => {
    if (isFormLocked) return;
    clearEditableError();
    setWorkspaceIds(current_0 => current_0.includes(workspaceId_0) ? current_0.filter(item_0 => item_0 !== workspaceId_0) : [...current_0, workspaceId_0]);
  };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const canRetryAmbiguousAttempt = isAmbiguousOutcome && previousAttempt !== null;
    if (!isFormValid && !canRetryAmbiguousAttempt || isPending || isConflict) return;
    const fingerprint = JSON.stringify({
      name: name.trim(),
      scopes,
      workspaceIds: form.workspaceIds,
      expiresAt
    });
    const reusableKey = previousAttempt?.fingerprint === fingerprint ? previousAttempt?.request.idempotencyKey : undefined;
    const request = canRetryAmbiguousAttempt ? previousAttempt.request : buildIssueRequest(form, reusableKey ?? createIdempotencyKey());
    setPreviousAttempt({
      fingerprint,
      request
    });
    setServerError(null);
    setIsPending(true);
    try {
      await onIssue(request);
      resetForm();
    } catch (error) {
      const code = readIssueErrorCode(error);
      const resolvedError: IssueServerError | null = code === "TOKEN_LIMIT_REACHED" ? "limit-reached" : code === "TOKEN_ALREADY_ISSUED" ? "already-issued" : mode === "admin" && code === "TOKEN_OWNER_NOT_FOUND" ? "owner-not-found" : code === "TOKEN_SCOPE_NOT_ADMITTED" ? "scope-not-admitted" : code === "TOKEN_WORKSPACE_NOT_ACCESSIBLE" ? "workspace-not-accessible" : code === "TOKEN_EXPIRY_OUT_OF_RANGE" ? "expiry-out-of-range" : null;
      if (resolvedError) {
        setPreviousAttempt(null);
        setServerError(resolvedError);
      } else {
        setServerError("ambiguous");
      }
    } finally {
      setIsPending(false);
    }
  };
  return <AlertDialogWrapper isOpen={isOpen} title={t("issue.title")} description={t("issue.description")} hideCloseButton closeDisabled={isPending} setIsOpenCallback={open => {
    if (!open) handleClose();
  }} footerNode={<div className="flex min-w-full justify-end gap-2">
          <Button type="button" variant="outline" disabled={isPending} onClick={handleClose}>
            {t("issue.cancel")}
          </Button>
          <Button type="submit" form={`${idPrefix}-form`} disabled={!isFormValid && !isAmbiguousOutcome || isPending || isConflict || isAmbiguousOutcome && previousAttempt === null}>
            {t(isAmbiguousOutcome ? "issue.retrySameAttempt" : "issue.submit")}
          </Button>
        </div>}>
      <form id={`${idPrefix}-form`} className="space-y-5" onSubmit={handleSubmit}>
        {mode === "admin" && adminContext && <div className="rounded-md border bg-muted/30 p-3 text-sm">
            <p>
              {t("issue.adminOwnerLabel")}:{" "}
              <span className="font-medium">{adminContext.ownerName}</span>
            </p>
            <p>
              {t("issue.adminIssuerLabel")}:{" "}
              <span className="font-medium">{adminContext.issuerName}</span>
            </p>
            <p className="mt-2 text-muted-foreground">{t("issue.adminRightsExplanation")}</p>
          </div>}
        <div className="grid gap-2">
          <Label htmlFor={`${idPrefix}-name`}>{t("issue.nameLabel")}</Label>
          <Input id={`${idPrefix}-name`} value={name} maxLength={128} required autoComplete="off" disabled={isFormLocked} onChange={event_0 => {
          if (isFormLocked) return;
          setName(event_0.target.value);
          clearEditableError();
        }} />
        </div>

        <fieldset className="space-y-2" disabled={isFormLocked}>
          <legend className="mb-2 text-sm font-medium">{t("issue.scopesLabel")}</legend>
          {visibleScopes.length === 0 ? <p className="text-sm text-muted-foreground">{t("issue.noScopes")}</p> : <div className="overflow-hidden rounded-md border">
              {(["api", "mcp"] as const).map(group => visibleScopes.some(scope_5 => scope_5.startsWith(`${group}_`)) && <div key={group} className="border-b last:border-b-0">
                      <p className="bg-muted/40 px-3 py-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                        {t(`issue.scopeGroups.${group}`)}
                      </p>
                      {visibleScopes.filter(scope_3 => scope_3.startsWith(`${group}_`)).map(scope_4 => {
              const id = `${idPrefix}-scope-${scope_4}`;
              return <div key={scope_4} className="flex items-center gap-2 border-t px-3 py-3">
                              <Checkbox id={id} checked={scopes.includes(scope_4)} onCheckedChange={() => toggleScope(scope_4)} />
                              <Label htmlFor={id}>
                                {t(`issue.scopes.${scope_4}`)}
                                {mode === "owner" && !allowedScopes.includes(scope_4) && <span className="ml-1 text-xs text-destructive">
                                    {t("issue.scopeNoLongerAdmitted")}
                                  </span>}
                              </Label>
                            </div>;
            })}
                    </div>)}
            </div>}
          {mcpAvailable && <p className="text-sm text-amber-700 dark:text-amber-400">{t("issue.mcpWarning")}</p>}
          {requiresDestroyConfirmation && adminContext && <AdminDestroyConfirmation expectedValue={adminContext.destroyConfirmationValue} value={destroyConfirmation} disabled={isFormLocked} onChange={value => {
          if (isFormLocked) return;
          setDestroyConfirmation(value);
          clearEditableError();
        }} />}
        </fieldset>

        <fieldset className="space-y-3" disabled={isFormLocked}>
          <legend className="mb-2 text-sm font-medium">{t("issue.workspacesLabel")}</legend>
          <RadioGroup value={workspaceChoice} onValueChange={value_0 => {
          if (isFormLocked) return;
          setWorkspaceChoice(value_0 as WorkspaceChoice);
          clearEditableError();
        }} className="gap-3">
            <div className="flex items-start gap-2 rounded-md border p-3">
              <RadioGroupItem id={`${idPrefix}-all`} value="all" />
              <Label htmlFor={`${idPrefix}-all`}>{t("issue.workspaces.all")}</Label>
            </div>
            <div className="flex items-start gap-2 rounded-md border p-3">
              <RadioGroupItem id={`${idPrefix}-selected`} value="selected" disabled={workspaceOptions.length === 0} />
              <Label htmlFor={`${idPrefix}-selected`}>{t("issue.workspaces.selected")}</Label>
            </div>
          </RadioGroup>
          {workspaceChoice === "selected" && workspaceOptions.length > 0 && <div className="grid gap-2 rounded-md border p-3 sm:grid-cols-2">
              {workspaceOptions.map(workspace => {
            const id_0 = `${idPrefix}-workspace-${workspace.id}`;
            return <div key={workspace.id} className="flex items-center gap-2">
                    <Checkbox id={id_0} checked={workspaceIds.includes(workspace.id)} onCheckedChange={() => toggleWorkspace(workspace.id)} />
                    <Label htmlFor={id_0}>{workspace.label}</Label>
                  </div>;
          })}
            </div>}
          {workspaceOptionsLoading && <p role="status" className="text-sm text-muted-foreground">
              {t("issue.loadingWorkspaceOptions")}
            </p>}
        </fieldset>
        {workspaceOptionsUnavailable && <div role="alert" className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-destructive">{t("issue.workspaceOptionsUnavailable")}</p>
            {onRetryWorkspaceOptions && <Button type="button" variant="outline" disabled={isRetryingWorkspaceOptions} onClick={onRetryWorkspaceOptions}>
                {t("issue.retryWorkspaceOptions")}
              </Button>}
          </div>}

        <div className="grid gap-2">
          <Label htmlFor={`${idPrefix}-expires-at`}>{t("issue.expiresAtLabel")}</Label>
          <div className="flex flex-wrap gap-2">
            {expiryPresets.map(({
            days,
            key
          }) => <Button key={key} type="button" variant="outline" size="sm" disabled={isFormLocked} onClick={() => {
            setExpiresAt(new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
            clearEditableError();
          }}>
                {t(`issue.expiryPresets.${key}`)}
              </Button>)}
          </div>
          <Input id={`${idPrefix}-expires-at`} type="date" value={expiresAt} required disabled={isFormLocked} onChange={event_1 => {
          if (isFormLocked) return;
          setExpiresAt(event_1.target.value);
          clearEditableError();
        }} />
          <p className="text-xs text-muted-foreground">{t("issue.expiryHint")}</p>
        </div>

        {expiresAt && validationErrors.expiresAt && <p role="alert" className="text-sm text-destructive">
            {t(`issue.validation.${expiryValidationKey}`)}
          </p>}
        {serverError && <div role="alert" className="space-y-2 text-sm text-destructive">
            <p>{t(getIssueErrorMessageKey(serverError, mode))}</p>
            {serverError === "scope-not-admitted" && mode === "owner" && onRetryAdmittedScopes && <Button type="button" variant="outline" disabled={isRetryingAdmittedScopes} onClick={onRetryAdmittedScopes}>
                {t("issue.retryScopes")}
              </Button>}
            {isConflict && <Button type="button" variant="outline" onClick={handleClose}>
                {t("issue.alreadyIssuedAction")}
              </Button>}
          </div>}
      </form>
    </AlertDialogWrapper>;
};